import React, { useState, useEffect, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, orderBy, query, where, addDoc } from 'firebase/firestore';

import learnModeIcon from '../../Icons/learn-mode.svg';
import { fetchCookbookSaveQuota, messageForCookbookQuotaCheckUnavailable } from '../../services/subscriptionQuotaService';
import {
  confirmCookbookDeckNotRemovedByQuotaTrigger,
  cookbookDeckRemovedByMonthlyLimitMessage,
} from '../../utils/cookbookDeckQuotaTriggerWatch';
import { ReactComponent as BookIcon } from '../../Icons/book.svg';
import { ReactComponent as LearnIcon } from '../../Icons/learn-mode.svg';
import IconRenderer from '../ui/IconRenderer';
import SVGRenderer from '../ui/SVGRenderer';
import './LearnMode.css';

const LearnMode = ({ userId, onModeChange, onCourseSelect, onShowCourseGenerator, onStartStudying }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    language: 'All',
    completion: 'All',
    difficulty: 'All',
    duration: 'All',
    tag: 'All'
  });
  const [sortBy, setSortBy] = useState('recent');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [selectedCourseForSettings, setSelectedCourseForSettings] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Helper function to calculate experience required for leveling (matches LevelingSystem.js)
  const calculateExpToLevel = (level) => {
    return Math.round(100 * Math.pow(10, (level - 1) / (99 / Math.log(10))));
  };

  useEffect(() => {
    loadUserCourses();
  }, [userId]);

  const loadUserCourses = async () => {
    try {
      setLoading(true);
      const coursesRef = collection(db, `users/${userId}/courses`);
      const q = query(coursesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const loadedCourses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setCourses(loadedCourses);
      setError('');
    } catch (error) {
      console.error('Error loading courses:', error);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };


  const handleCourseSelect = (course) => {
    onCourseSelect(course);
  };

  const createCookbookFromCourse = async (course) => {
    try {
      console.log('Creating cookbook for course:', course.id);

      const quota = await fetchCookbookSaveQuota();
      if (quota.quotaCheckUnavailable) {
        throw new Error(messageForCookbookQuotaCheckUnavailable(quota));
      }
      if (!quota.isPremium && quota.allowed === false) {
        throw new Error(
          `Free accounts can create up to ${quota.limit} new cookbooks per month (UTC). Upgrade to Pro for unlimited cookbooks and full features.`
        );
      }

      const courseLanguage = course.language || 'javascript';
      const courseTopic = course.topic || 'general_programming';
      const timestamp = new Date();

      // Create the cookbook document
      const cookbookData = {
        name: `${course.title} - Code Cookbook`,
        description: `A collection of code snippets from the "${course.title}" course. Practice these examples to master ${courseLanguage} programming concepts.`,
        language: courseLanguage,
        type: 'cookbook',
        tags: [courseTopic, course.difficulty, 'course', 'cookbook'],
        // Course-specific metadata
        courseId: course.id,
        courseTitle: course.title,
        // Initialize leveling fields
        level: 1,
        current_exp: 0,
        exp_to_level: calculateExpToLevel(2),
        // Timestamps
        createdAt: timestamp,
        updatedAt: timestamp,
        lastAccessed: timestamp,
        userId: userId
      };

      const decksRef = collection(db, `users/${userId}/decks`);
      const cookbookRef = await addDoc(decksRef, cookbookData);
      const cookbookId = cookbookRef.id;

      console.log(`Created cookbook: ${cookbookId}`);

      if (!quota.isPremium) {
        const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(cookbookRef);
        if (!outcome.ok && outcome.removedByMonthlyLimit) {
          throw new Error(cookbookDeckRemovedByMonthlyLimitMessage);
        }
      }

      // Create cards for each code snippet from the course
      const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
      let cardCount = 0;

      if (course.syllabus && Array.isArray(course.syllabus)) {
        for (const lesson of course.syllabus) {
          if (lesson.subLessons && Array.isArray(lesson.subLessons)) {
            for (const subLesson of lesson.subLessons) {
              if (subLesson.codeSnippet && subLesson.codeSnippet.trim()) {
                const cardData = {
                  title: `${lesson.title} - ${subLesson.title}`,
                  description: subLesson.description || `Code example from ${lesson.title}`,
                  solution: subLesson.codeSnippet,
                  tags: [courseTopic, course.difficulty, lesson.title.toLowerCase().replace(/\s+/g, '_')],
                  // Course-specific metadata
                  courseId: course.id,
                  lessonId: lesson.id,
                  subLessonId: subLesson.id,
                  courseTitle: course.title,
                  lessonTitle: lesson.title,
                  subLessonTitle: subLesson.title,
                  // Initialize leveling fields
                  level: 1,
                  current_exp: 0,
                  exp_to_level: calculateExpToLevel(2),
                  currentLineIndex: 0,
                  // Timestamps
                  createdAt: timestamp,
                  updatedAt: timestamp,
                  creationSource: 'course',
                };

                await addDoc(cardsRef, cardData);
                cardCount++;
                console.log(`Created card for: ${subLesson.title}`);
              }
            }
          }
        }
      }

      console.log(`Successfully created cookbook with ${cardCount} cards`);
      return cookbookId;

    } catch (error) {
      console.error('Error creating course cookbook:', error);
      throw error;
    }
  };

  const handleStudyCookbook = async (course) => {
    try {
      // Find the cookbook associated with this course
      const decksRef = collection(db, `users/${userId}/decks`);
      const q = query(decksRef, where('courseId', '==', course.id), where('type', '==', 'cookbook'));
      const querySnapshot = await getDocs(q);

      let cookbookId;

      if (querySnapshot.empty) {
        console.log('No cookbook found for course, creating one in background...');
        // Create a new cookbook for this course in the background
        createCookbookFromCourse(course).then(cookbookId => {
          console.log('Cookbook created successfully:', cookbookId);
          // Start studying the cookbook after creation
          onStartStudying(cookbookId);
        }).catch(error => {
          console.error('Error creating cookbook in background:', error);
          const msg = error?.message;
          if (msg) alert(msg);
        });
        return;
      } else {
        // Get the first cookbook (there should only be one per course)
        const cookbookDoc = querySnapshot.docs[0];
        const cookbookData = { id: cookbookDoc.id, ...cookbookDoc.data() };
        cookbookId = cookbookData.id;
        console.log('Found existing cookbook for course:', cookbookData.name);
      }

      // Start studying the cookbook
      onStartStudying(cookbookId);
    } catch (error) {
      console.error('Error handling cookbook for course:', error);
      // You could show an error toast notification here if desired
    }
  };

  // Helper to infer language consistently across UI
  const getCourseLanguage = (course) => {
    if (course.language) return course.language;
    const title = (course.title || '').toLowerCase();
    if (title.includes('javascript')) return 'JavaScript';
    if (title.includes('python')) return 'Python';
    if (title.includes('java') && !title.includes('javascript')) return 'Java';
    if (title.includes('c++')) return 'C++';
    if (title.includes('c#')) return 'C#';
    if (title.includes('ruby')) return 'Ruby';
    return null;
  };

  const getCourseProgressRatio = (course) => {
    const completedCount = Object.keys(course.progress?.completedLessons || {}).length;
    const total = course.syllabus?.length || 0;
    return total === 0 ? 0 : completedCount / total;
  };

  const getCourseTags = (course) => {
    if (!Array.isArray(course.tags)) return [];
    return course.tags
      .map(t => String(t).toLowerCase())
      .filter(Boolean);
  };

  const courseDurationBucket = (course) => {
    // expected estimatedDuration like "2-4 weeks" or "1-2 weeks"
    const text = (course.estimatedDuration || '').toLowerCase();
    if (!text) return 'Unknown';
    // rough parsing: extract first number
    const match = text.match(/(\d+)/);
    const weeks = match ? parseInt(match[1], 10) : 0;
    if (weeks && weeks < 2) return 'Short';
    if (weeks && weeks <= 6) return 'Medium';
    if (weeks && weeks > 6) return 'Long';
    // fallback by keywords
    if (text.includes('short')) return 'Short';
    if (text.includes('long')) return 'Long';
    return 'Medium';
  };

  // Derived lists and stats
  const filteredAndSortedCourses = useMemo(() => {
    const list = [...courses].filter((course) => {
      const language = getCourseLanguage(course) || 'Unknown';
      const ratio = getCourseProgressRatio(course);
      const isCompleted = (course.syllabus?.length || 0) > 0 && ratio >= 1;
      const courseTags = getCourseTags(course);

      // Language filter
      if (filters.language !== 'All' && language !== filters.language) return false;

      // Completion filter
      if (filters.completion === 'In Progress' && (ratio === 0 || isCompleted)) return false;
      if (filters.completion === 'Not Started' && ratio !== 0) return false;
      if (filters.completion === 'Completed' && !isCompleted) return false;

      // Difficulty filter
      if (filters.difficulty !== 'All' && (course.difficulty || '').toLowerCase() !== filters.difficulty.toLowerCase()) return false;

      // Duration filter
      if (filters.duration !== 'All') {
        const bucket = courseDurationBucket(course);
        if (bucket !== filters.duration) return false;
      }

      // Tag filter (single)
      if (filters.tag && filters.tag !== 'All') {
        const wanted = String(filters.tag).toLowerCase();
        const hasTag = courseTags.some(t => String(t).toLowerCase() === wanted);
        if (!hasTag) return false;
      }

      return true;
    });

    // Sort
    if (sortBy === 'progress') {
      list.sort((a, b) => getCourseProgressRatio(b) - getCourseProgressRatio(a));
    } else if (sortBy === 'difficulty') {
      const order = { beginner: 0, intermediate: 1, advanced: 2 };
      list.sort((a, b) => (order[(a.difficulty || '').toLowerCase()] ?? 9) - (order[(b.difficulty || '').toLowerCase()] ?? 9));
    } else {
      // recent (already fetched desc by createdAt). As a fallback, use updatedAt/createdAt
      list.sort((a, b) => {
        const aTime = (a.createdAt?.seconds || a.updatedAt?.seconds || 0);
        const bTime = (b.createdAt?.seconds || b.updatedAt?.seconds || 0);
        return bTime - aTime;
      });
    }

    return list;
  }, [courses, filters, sortBy]);

  const overallCompleted = useMemo(() => {
    if (!courses.length) return { completed: 0, total: 0, pct: 0 };
    const completed = courses.filter(c => {
      const ratio = getCourseProgressRatio(c);
      return (c.syllabus?.length || 0) > 0 && ratio >= 1;
    }).length;
    const pct = Math.round((completed / courses.length) * 100) || 0;
    return { completed, total: courses.length, pct };
  }, [courses]);

  // Courses filtered for language summary (respect current filters except the language itself)
  const coursesForLanguageSummary = useMemo(() => {
    return courses.filter((course) => {
      const ratio = getCourseProgressRatio(course);
      const isCompleted = (course.syllabus?.length || 0) > 0 && ratio >= 1;
      const courseTags = getCourseTags(course);

      // Completion filter
      if (filters.completion === 'In Progress' && (ratio === 0 || isCompleted)) return false;
      if (filters.completion === 'Not Started' && ratio !== 0) return false;
      if (filters.completion === 'Completed' && !isCompleted) return false;

      // Difficulty filter
      if (filters.difficulty !== 'All' && (course.difficulty || '').toLowerCase() !== filters.difficulty.toLowerCase()) return false;

      // Duration filter
      if (filters.duration !== 'All') {
        const bucket = courseDurationBucket(course);
        if (bucket !== filters.duration) return false;
      }

      // Tag filter
      if (filters.tag && filters.tag !== 'All') {
        const wanted = String(filters.tag).toLowerCase();
        const hasTag = courseTags.some(t => String(t).toLowerCase() === wanted);
        if (!hasTag) return false;
      }

      return true;
    });
  }, [courses, filters.completion, filters.difficulty, filters.duration, filters.tag]);

  // Build language aggregates for header summary based on current filters
  const languageSummary = useMemo(() => {
    const summary = {};
    for (const c of coursesForLanguageSummary) {
      const lang = getCourseLanguage(c) || 'Unknown';
      if (!summary[lang]) summary[lang] = { total: 0, completed: 0 };
      summary[lang].total += 1;
      if ((c.syllabus?.length || 0) > 0 && getCourseProgressRatio(c) >= 1) {
        summary[lang].completed += 1;
      }
    }
    return summary;
  }, [coursesForLanguageSummary]);

  const availableTags = useMemo(() => {
    const set = new Set();
    courses.forEach(c => getCourseTags(c).forEach(t => t && set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [courses]);

  const toggleCourseExpanded = (courseId) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId); else next.add(courseId);
      return next;
    });
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      // Delete the course document from Firestore
      const courseRef = doc(db, `users/${userId}/courses/${courseId}`);
      await deleteDoc(courseRef);

      // Update local state to remove the deleted course
      setCourses(prevCourses => prevCourses.filter(course => course.id !== courseId));

      console.log('Course and associated snippets deleted successfully');
    } catch (error) {
      console.error('Error deleting course:', error);
      setError('Failed to delete course');
      throw error; // Re-throw so the CourseViewer can handle the error
    }
  };

  const handleResetCourseProgress = async (courseId) => {
    try {
      const courseRef = doc(db, `users/${userId}/courses/${courseId}`);
      await updateDoc(courseRef, {
        progress: {
          completedLessons: {},
          currentLesson: 0,
          currentSubLesson: 0,
          understoodCards: {}
        },
        updatedAt: new Date()
      });

      // Update local state
      setCourses(prevCourses =>
        prevCourses.map(course =>
          course.id === courseId
            ? {
              ...course,
              progress: {
                completedLessons: {},
                currentLesson: 0,
                currentSubLesson: 0,
                understoodCards: {}
              }
            }
            : course
        )
      );

      console.log('Course progress reset successfully');
    } catch (error) {
      console.error('Error resetting course progress:', error);
      setError('Failed to reset course progress');
    }
  };

  const handleSettingsClick = (e, course) => {
    e.stopPropagation();
    setSelectedCourseForSettings(course);
    setShowSettingsMenu(true);
  };

  const closeSettingsMenu = () => {
    setShowSettingsMenu(false);
    setSelectedCourseForSettings(null);
    setShowDeleteConfirm(false);
    setShowResetConfirm(false);
  };





  const updateCourseProgress = async (courseId, lessonIndex, subLessonIndex, completed) => {
    try {
      const courseRef = doc(db, `users/${userId}/courses/${courseId}`);

      await updateDoc(courseRef, {
        [`progress.completedLessons.${lessonIndex}.${subLessonIndex}`]: completed,
        [`progress.currentLesson`]: lessonIndex,
        [`progress.currentSubLesson`]: subLessonIndex,
        updatedAt: new Date()
      });

      // Update local state
      setCourses(prevCourses =>
        prevCourses.map(course =>
          course.id === courseId
            ? {
              ...course,
              progress: {
                ...course.progress,
                completedLessons: {
                  ...course.progress.completedLessons,
                  [lessonIndex]: {
                    ...course.progress.completedLessons[lessonIndex],
                    [subLessonIndex]: completed
                  }
                },
                currentLesson: lessonIndex,
                currentSubLesson: subLessonIndex
              }
            }
            : course
        )
      );

      // Update selected course if it's the current one
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            completedLessons: {
              ...prev.progress.completedLessons,
              [lessonIndex]: {
                ...prev.progress.completedLessons[lessonIndex],
                [subLessonIndex]: completed
              }
            },
            currentLesson: lessonIndex,
            currentSubLesson: subLessonIndex
          }
        }));
      }

    } catch (error) {
      console.error('Error updating course progress:', error);
      setError('Failed to update progress');
    }
  };

  // if (loading) {
  //   return (
  //     <div className="learn-mode-container">
  //       <div className="loading-spinner">
  //         <div className="spinner"></div>
  //         <p>Loading your learning journey...</p>
  //       </div>
  //     </div>
  //   );
  // }



  return (
    <div className="learn-mode-container">
      <div className="learn-mode-header">
        <div className="learn-mode-header-content">
          <img src={learnModeIcon} alt="Learn Mode" className="learn-mode-icon" />
          <div>
            <h1>My Courses</h1>
            {/* <div className="learn-mode-subtitle">Continue learning or explore new topics</div> */}
          </div>
        </div>
        <div className="learn-mode-header-actions">
          {/* <div className="overall-progress">
            <div className="progress-bar small">
              <div className="progress-fill" style={{ width: `${overallCompleted.pct}%` }}></div>
            </div>
            <span className="progress-text">{overallCompleted.completed}/{overallCompleted.total} completed</span>
          </div> */}
          <div className="filters-total" title={`Filtered: ${filteredAndSortedCourses.length} of ${courses.length}`}>
            <span className="filters-total-label"></span>
            <span className="filters-total-count">{filteredAndSortedCourses.length}</span>
            <span className="filters-total-slash">/</span>
            <span className="filters-total-total">{courses.length}</span>
          </div>
          <button
            className="create-course-btn"
            onClick={onShowCourseGenerator}
          >
            + Add Course
          </button>
        </div>
      </div>



      {/* Filters / Sorting */}
      <div className="courses-filters">
        <div className="filters-left">
          {/* <label>
            Language
            <select value={filters.language} onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value }))}>
              <option>All</option>
              <option>Python</option>
              <option>JavaScript</option>
              <option>Java</option>
              <option>C++</option>
              <option>C#</option>
              <option>Ruby</option>
            </select>
          </label> */}
          <label>
            <span className="tag-filter-label"><svg className="filter-icon tag-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21 10.59L12.59 2H4v8.59L12.41 19a2 2 0 0 0 2.83 0l5.76-5.76a2 2 0 0 0 0-2.83zM7 7.5A1.5 1.5 0 1 1 7 4.5a1.5 1.5 0 0 1 0 3z" /></svg>Tag</span>
            <select
              value={filters.tag}
              onChange={(e) => setFilters(prev => ({ ...prev, tag: e.target.value }))}
            >
              <option>All</option>
              {availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="filter-label"><svg className="filter-icon completion-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>Completion</span>
            <select value={filters.completion} onChange={(e) => setFilters(prev => ({ ...prev, completion: e.target.value }))}>
              <option>All</option>
              <option>In Progress</option>
              <option>Not Started</option>
              <option>Completed</option>
            </select>
          </label>
          <label>
            <span className="filter-label"><svg className="filter-icon difficulty-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>Difficulty</span>
            <select value={filters.difficulty} onChange={(e) => setFilters(prev => ({ ...prev, difficulty: e.target.value }))}>
              <option>All</option>
              <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option>
            </select>
          </label>
          {/* <label>
            Duration
            <select value={filters.duration} onChange={(e) => setFilters(prev => ({ ...prev, duration: e.target.value }))}>
              <option>All</option>
              <option>Short</option>
              <option>Medium</option>
              <option>Long</option>
            </select>
          </label> */}

        </div>
        <div className="filters-right">

          <label>
            <span className="filter-label"><svg className="filter-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 18h6v-2H3v2zm0-5h12v-2H3v2zm0-7v2h18V6H3z" /></svg>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Recently Added</option>
              <option value="progress">Progress</option>
              <option value="difficulty">Difficulty</option>
            </select>
          </label>
        </div>
      </div>
      {/* Language summary chips */}
      <div className="language-summary">
        {Object.keys(languageSummary).length === 0 ? (
          <div className="language-summary-empty">No courses yet</div>
        ) : (
          Object.entries(languageSummary)
            .sort((a, b) => (b[1].total - a[1].total))
            .map(([lang, stats]) => {
              return (
                <button
                  key={lang}
                  className={`language-chip ${filters.language === lang ? 'active' : ''}`}
                  onClick={() => setFilters(prev => ({ ...prev, language: prev.language === lang ? 'All' : lang }))}
                  title={`${lang}`}
                >
                  <IconRenderer name={lang} type="language" className="chip-icon" />
                  <span className="chip-name">{lang}</span>
                  <span className="chip-counts">{stats.completed}/{stats.total}</span>
                </button>
              );
            })
        )}
      </div>



      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="error-dismiss">×</button>
        </div>
      )}

      <div className="courses-section">
        {filteredAndSortedCourses.length > 0 ? (
          <div className="existing-courses">

            <div className="courses-grid">
              {filteredAndSortedCourses.map(course => (
                <div
                  key={course.id}
                  className={`course-card ${expandedCards.has(course.id) ? 'expanded' : 'collapsed'}`}
                >
                  <div className="course-header" onClick={() => toggleCourseExpanded(course.id)}>
                    <div className="course-title-section">
                      {(() => {
                        const language = getCourseLanguage(course);
                        return language ? (
                          <IconRenderer name={language} type="language" className="course-language-icon" />
                        ) : null;
                      })()}
                      {/* <SVGRenderer
                        svgCode={course.svgIcon}
                        fallbackType="course"
                        width={24}
                        height={24}
                        className="course-card-icon"
                      /> */}
                      <h3>{course.title}</h3>
                      {/* Debug: Show available course properties */}
                      {console.log('Course data:', course)}
                    </div>
                    <div className="course-header-actions">

                      <div className="header-progress count-only">
                        <span className="lesson-count">{Object.keys(course.progress?.completedLessons || {}).length} / {course.syllabus?.length || 0} lessons</span>
                      </div>
                      <button className="continue-btn" onClick={(e) => { e.stopPropagation(); handleCourseSelect(course); }}>
                        <span className="btn-icon">
                          <LearnIcon width="16" height="16" />
                        </span>
                        Continue Learning
                      </button>
                      <button
                        className="study-cookbook-btn"
                        onClick={(e) => { e.stopPropagation(); handleStudyCookbook(course); }}
                      >
                        <span className="btn-icon">⚡</span>
                        Study
                      </button>
                      <button
                        className={`collapse-toggle ${expandedCards.has(course.id) ? 'open' : ''}`}
                        aria-label={expandedCards.has(course.id) ? 'Collapse' : 'Expand'}
                        onClick={(e) => { e.stopPropagation(); toggleCourseExpanded(course.id); }}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                          <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                        </svg>
                      </button>

                    </div>
                  </div>
                  {/* Course tags - show in collapsed state only */}
                  {!expandedCards.has(course.id) && (Array.isArray(course.tags) && course.tags.length > 0) && (
                    <div className="course-tags">
                      {course.tags.slice(0, 4).map(tag => (
                        <span key={tag} className="course-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  {expandedCards.has(course.id) && (
                    <div className="course-body">
                      <p className="course-description">{course.description}</p>
                      <div className="course-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${getCourseProgressRatio(course) * 100}%`
                            }}
                          ></div>
                        </div>
                        {/* Course tags - show in expanded state above progress actions */}
                        {(Array.isArray(course.tags) && course.tags.length > 0) && (
                          <div className="course-tags">
                            {course.tags.slice(0, 4).map(tag => (
                              <span key={tag} className="course-tag">{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="course-progress-actions">
                          <button
                            className="course-settings-btn"
                            onClick={(e) => handleSettingsClick(e, course)}
                            title="Course Settings"
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                              <path fill="currentColor" d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5M19.43 12.97c.04-.32.07-.64.07-.97s-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1s.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z" />
                            </svg>
                          </button>
                          <span className="progress-text">
                            {Object.keys(course.progress?.completedLessons || {}).length} / {course.syllabus?.length || 0} lessons
                          </span>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-content">
              <h2>No courses yet</h2>
              <p>Start your learning journey by creating your first personalized course!</p>
              <button
                className="create-first-course-btn"
                onClick={onShowCourseGenerator}
              >
                Create Your First Course
              </button>
            </div>
          </div>
        )}
      </div>

      {/* <div className="learn-mode-footer">
        <button
          className="back-to-study-btn"
          onClick={() => onModeChange('regular')}
        >
          ← Back to Study Mode
        </button>
      </div> */}

      {/* Settings Menu */}
      {showSettingsMenu && selectedCourseForSettings && (
        <div className="course-settings-overlay" onClick={closeSettingsMenu}>
          <div className="course-settings-menu" onClick={(e) => e.stopPropagation()}>
            <div className="course-settings-header">
              <h3>Course Settings</h3>
              <button className="close-settings-btn" onClick={closeSettingsMenu}>×</button>
            </div>
            <div className="course-settings-content">
              <p className="course-settings-course-name">{selectedCourseForSettings.title}</p>
              <div className="course-settings-options">
                <button
                  className="course-settings-option reset-option"
                  onClick={() => setShowResetConfirm(true)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                  </svg>
                  Reset Progress
                </button>
                <button
                  className="course-settings-option delete-option"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                  Delete Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Progress Confirmation */}
      {showResetConfirm && selectedCourseForSettings && (
        <div className="course-confirm-overlay">
          <div className="course-confirm-dialog">
            <h3>Reset Course Progress</h3>
            <p>Are you sure you want to reset the progress for "{selectedCourseForSettings.title}"? This will clear all completed lessons and start from the beginning.</p>
            <div className="course-confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn reset-btn"
                onClick={async () => {
                  await handleResetCourseProgress(selectedCourseForSettings.id);
                  closeSettingsMenu();
                }}
              >
                Reset Progress
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Confirmation */}
      {showDeleteConfirm && selectedCourseForSettings && (
        <div className="course-confirm-overlay">
          <div className="course-confirm-dialog">
            <h3>Delete Course</h3>
            <p>Are you sure you want to delete "{selectedCourseForSettings.title}"? This action cannot be undone and will also delete all associated snippets.</p>
            <div className="course-confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn delete-btn"
                onClick={async () => {
                  await handleDeleteCourse(selectedCourseForSettings.id);
                  closeSettingsMenu();
                }}
              >
                Delete Course
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LearnMode;
